import { FC } from 'react';
import {
  Button,
  ButtonProps,
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps as ChakraModalProps,
} from '@chakra-ui/react';
import { CloseMenu } from '../../assets';
import { getDataTracking } from '../../utils';
import { TrackingLabelProps } from '../../types/global';

export interface ModalProps extends ChakraModalProps, TrackingLabelProps {
  buttonProps?: ButtonProps;
  hasFooter?: boolean;
  modalTitle?: JSX.Element | string;
  hasPadding?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
}

/**
 * @description FC Modal wrapper for Chakra
 * @param buttonProps
 * @param modalTitle string
 * @param hasFooter boolean
 * @param isOpen boolean
 * @param hasPadding boolean defaults to true
 * @param onCancel callback
 * @param onClose callback
 * @param onSave callback
 */
export const Modal: FC<ModalProps> = ({
  buttonProps,
  children: modalBody,
  hasFooter,
  isOpen,
  modalTitle,
  hasPadding = true,
  onCancel,
  onClose,
  onSave,
  trackingLabel,
  ...rest
}) => (
  <>
    {buttonProps && (
      <Button
        {...buttonProps}
        {...getDataTracking(`${trackingLabel || buttonProps.title}ModalLink`)}
      >
        {buttonProps.title}
      </Button>
    )}

    <ChakraModal {...{ isOpen, onClose }} {...rest}>
      <ModalOverlay />
      <ModalContent borderRadius="8px" bg="white">
        <ModalHeader
          fontWeight="medium"
          borderBottom="1px solid var(--chakra-colors-brand-primary-lightGrey)"
          mt={modalTitle ? '0px' : '1.5rem'}
          pt="0.5rem"
        >
          {modalTitle}
        </ModalHeader>
        <ModalCloseButton
          {...getDataTracking(
            `${trackingLabel || buttonProps?.title}modalCloseButton}`
          )}
        >
          <CloseMenu boxSize="24px" />
        </ModalCloseButton>
        <ModalBody p={hasPadding ? 4 : 0}>{modalBody}</ModalBody>

        {hasFooter && (
          <ModalFooter display="flex" flexDirection="column" gap={3}>
            <Button w="100%" colorScheme="blue" onClick={onSave}>
              Save
            </Button>
            <Button
              w="100%"
              variant="ghost"
              colorScheme="blue"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </ChakraModal>
  </>
);
